import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, DataTable, EmptyState, FilterBar, FilterChips, Pagination, Pill, fmtBRL, type Column } from "@atlas/ui/web";
import type { EstadoPortabilidade, Portabilidade } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { POR_PAGINA_PADRAO, recorteDoServidor } from "../../lib/recorte";
import { useDebouncedValue } from "../../lib/use-debounce";
import { useFiltros } from "../../lib/use-filtros";
import { mensagemDeErro } from "../../lib/erro-validacao";
import { fmtDataHora } from "../../lib/refinanciamento";
import { ESTADOS_PORTABILIDADE, ESTADO_PORTABILIDADE_OPERADOR, pillDaPortabilidade, prazoDaEtapa } from "../../lib/portabilidade";
import { AnalisarComprovanteModal, PortabilidadeDetalheModal } from "../../components/PortabilidadeModais";

/**
 * Visao global do rito de portabilidade: todas as portabilidades, de todos os
 * bancos, com a etapa e quem precisa agir. A averbadora analisa os comprovantes
 * de credoras fora de operação. A página vem recortada da API.
 */
export function AverbadoraPortabilidade() {
  const qc = useQueryClient();
  const [filaManual, setFilaManual] = useState(false);
  const [analise, setAnalise] = useState<Portabilidade | null>(null);
  const [estado, setEstado] = useState<"" | EstadoPortabilidade>("");
  const [busca, setBusca] = useState("");
  const buscaDebounced = useDebouncedValue(busca);
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_PADRAO);
  const [detalhe, setDetalhe] = useState<string | null>(null);

  const filtros = useFiltros(
    [
      { nome: "busca", ligado: busca !== "", limpar: () => setBusca("") },
      { nome: "estado", ligado: estado !== "", limpar: () => setEstado("") },
    ],
    setPagina,
  );
  const { filtrar } = filtros;

  const q = useQuery({
    queryKey: ["admin", "portabilidade", estado || "todas", buscaDebounced, pagina, porPagina, filaManual],
    queryFn: () =>
      atlas.portabilidade.todas({
        estado: estado || undefined,
        analiseManual: filaManual || undefined,
        q: buscaDebounced || undefined,
        limit: porPagina,
        offset: (pagina - 1) * porPagina,
      }),
    refetchInterval: 15000,
    // Sem isto a tabela pisca vazia a cada virada de pagina.
    placeholderData: (anterior) => anterior,
  });
  const daPagina = recorteDoServidor(q.data?.itens ?? [], q.data?.total ?? 0, pagina, porPagina);

  const columns: Column<Portabilidade>[] = [
    {
      key: "estado",
      header: "Etapa",
      render: (p) => (
        <>
          <Pill variant={pillDaPortabilidade(p.estado)}>{p.analiseManual && p.estado === "aguardando_liquidacao" ? "Comprovante em análise" : ESTADO_PORTABILIDADE_OPERADOR[p.estado]}</Pill>
          {prazoDaEtapa(p.expiraEm) ? <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 4 }}>{prazoDaEtapa(p.expiraEm)}</div> : null}
        </>
      ),
    },
    {
      key: "id",
      header: "Portabilidade",
      mono: true,
      render: (p) => (
        <button type="button" onClick={() => setDetalhe(p.id)} style={{ all: "unset", cursor: "pointer", textDecoration: "underline" }}>
          {p.id}
        </button>
      ),
    },
    {
      key: "servidor",
      header: "Servidor",
      render: (p) => (
        <span>
          {p.servidorNome}
          <span style={{ display: "block", fontSize: 11, color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
            {p.matricula} · {p.cpfMasked}
          </span>
        </span>
      ),
    },
    { key: "unidade", header: "Órgão/entidade", render: (p) => p.unidadeNome || "—" },
    {
      key: "bancos",
      header: "Credora → proponente",
      render: (p) => (
        <span>
          {p.credora.nome} → {p.proponente.nome}
          <span style={{ display: "block", fontSize: 11, color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
            {p.origem.adf}{p.adfDestino ? ` → ${p.adfDestino}` : ""}
          </span>
        </span>
      ),
    },
    { key: "parcela", header: "Parcela atual", align: "right", render: (p) => fmtBRL(p.origem.parcela) },
    { key: "saldo", header: "Saldo informado", align: "right", render: (p) => (p.saldoInformado == null ? "—" : fmtBRL(p.saldoInformado)) },
    { key: "solicitadoPor", header: "Aberta por", render: (p) => (p.solicitadoPor === "servidor" ? "Servidor" : "Proponente") },
    { key: "criadoEm", header: "Aberta em", render: (p) => fmtDataHora(p.criadoEm) },
    { key: "analise", header: "Análise manual", render: (p) => p.analiseManual && p.estado === "aguardando_liquidacao" ? <Button size="sm" onClick={() => setAnalise(p)}>Analisar comprovante</Button> : "—" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header>
        <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
          Averbadora
        </span>
        <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>Portabilidade</h1>
        <p style={{ color: "var(--text-muted)", margin: "6px 0 0", maxWidth: 820, fontSize: 13 }}>
          A consignatária de destino (proponente) ou o servidor abre; o servidor confirma o que o proponente abriu; a credora informa o saldo; o
          proponente informa o pagamento; a credora liquida — e o contrato antigo é quitado com o novo nascendo averbado, na mesma
          transação. Se a credora estiver inativa, a averbadora analisa o comprovante e liquida. Atrasos das consignatárias bloqueiam novas averbações até a regularização; a análise manual não penaliza a consignatária.
        </p>
      </header>

      <FilterChips label="Fila" value={filaManual ? "manual" : "todas"} onChange={(v) => { setFilaManual(v === "manual"); setEstado(""); setPagina(1); }} options={[{ value: "todas", label: "Todas as portabilidades" }, { value: "manual", label: "Comprovantes para análise" }]} />
      <FilterBar
        layout="linha"
        searchValue={busca}
        onSearchChange={filtrar(setBusca)}
        searchPlaceholder="Buscar por portabilidade, ADF ou matrícula"
        {...filtros.propsFilterBar}
        actions={
          <FilterChips
            variant="segmentado"
            label="Etapa"
            value={estado}
            onChange={(v) => filtrar(setEstado)(v as "" | EstadoPortabilidade)}
            options={[
              { value: "", label: "Todas" },
              ...ESTADOS_PORTABILIDADE.map((e) => ({ value: e, label: ESTADO_PORTABILIDADE_OPERADOR[e] })),
            ]}
          />
        }
      />

      {q.isError ? (
        <div style={{ padding: "12px 16px", borderRadius: 10, border: "1px solid var(--danger-500)", color: "var(--danger-500)", fontSize: 13 }}>
          {mensagemDeErro(q.error, "Não foi possível carregar as portabilidades.")}
        </div>
      ) : null}
      <DataTable
        columns={columns}
        rows={daPagina.itens}
        rowKey={(p) => p.id}
        emptyState={
          <EmptyState
            motivo={daPagina.total > 0 ? null : busca || estado ? "filtro-vazio" : "sem-dados"}
            filtroVazio="Nenhuma portabilidade com esses filtros."
            semDados="Nenhuma portabilidade aberta ainda."
            onLimpar={filtros.limparFiltros}
          />
        }
      />
      <Pagination
        total={daPagina.total}
        pagina={daPagina.pagina}
        porPagina={porPagina}
        onPagina={setPagina}
        onPorPagina={setPorPagina}
        rotulo="portabilidades"
      />
      {analise ? <AnalisarComprovanteModal p={analise} onClose={() => setAnalise(null)} onFeito={() => { setAnalise(null); void qc.invalidateQueries({ queryKey: ["admin", "portabilidade"] }); void qc.invalidateQueries({ queryKey: ["portabilidade", "detalhe"] }); }} /> : null}
      {detalhe ? <PortabilidadeDetalheModal id={detalhe} carregar={atlas.portabilidade.detalheAdmin} onClose={() => setDetalhe(null)} /> : null}
    </div>
  );
}
